import { Form, ErrorMessage } from "@/shared/components";
import LinearProgress from "@mui/material/LinearProgress";
import { useQuery } from "@apollo/client";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import { useState, useMemo, useEffect, useRef, useCallback } from "react";

/** Типы полей схемы, которые рендерятся через TextField */
const DEBOUNCED_FIELD_TYPES = ["text", "password", "email", "url", "number"];

const DEFAULT_FILTER_DEBOUNCE_MS = 300;

interface AutoFilterFormProps {
  onSubmit: (values: any) => void;
  initialValues?: Record<string, any>;
  schema: any;
  title?: string;
}

function AutoFilterForm({
  onSubmit,
  initialValues = {},
  schema,
  title,
}: AutoFilterFormProps) {
  /**
   * Правки пользователя уходят наверх и возвращаются обратно через URL. Без
   * этого флага возврат затирал бы форму прямо во время ввода.
   */
  const isInternalChangeRef = useRef(false);

  const [expanded, setExpanded] = useState(false);
  const [controlledInitialValues, setControlledInitialValues] =
    useState<Record<string, any>>(initialValues);

  const {
    visibleSchema,
    hiddenSchema,
    hasHiddenFields,
    hasActiveHiddenFilters,
  } = useMemo(() => {
    const visible: Record<string, any> = {};
    const hidden: Record<string, any> = {};

    Object.entries(schema).forEach(([key, field]: [string, any]) => {
      if (field.priority === "secondary") {
        hidden[key] = field;
      } else {
        visible[key] = field;
      }
    });

    const activeHiddenFilters = Object.keys(hidden).some((key) => {
      const value = initialValues[key];
      if (value === null || value === undefined || value === "") return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    });

    return {
      visibleSchema: visible,
      hiddenSchema: hidden,
      hasHiddenFields: Object.keys(hidden).length > 0,
      hasActiveHiddenFilters: activeHiddenFilters,
    };
  }, [schema, initialValues]);

  const currentSchema = useMemo(() => {
    const schemaForState = expanded
      ? { ...visibleSchema, ...hiddenSchema }
      : visibleSchema;

    // Фильтр применяется на лету, поэтому текстовый ввод обязан быть
    // отложенным — иначе каждое нажатие клавиши уходит в запрос
    return Object.fromEntries(
      Object.entries(schemaForState).map(([key, field]) => {
        if (!field || typeof field !== "object") return [key, field];

        const type = (field as any).type ?? "text";
        if (!DEBOUNCED_FIELD_TYPES.includes(type)) return [key, field];
        if ((field as any).debounce !== undefined) return [key, field];

        return [key, { ...field, debounce: DEFAULT_FILTER_DEBOUNCE_MS }];
      })
    );
  }, [expanded, visibleSchema, hiddenSchema]);

  const hasValues = useMemo(
    () =>
      Object.values(initialValues).some((value) => {
        if (value === null || value === undefined || value === "") return false;
        if (Array.isArray(value) && value.length === 0) return false;
        return true;
      }),
    [initialValues]
  );

  // Синхронизируем форму только с внешними изменениями фильтра — например,
  // при переходе по истории браузера или восстановлении из localStorage
  useEffect(() => {
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
      return;
    }

    setControlledInitialValues(initialValues);
  }, [initialValues]);

  // Раскрываем скрытые фильтры, если среди них есть активные
  useEffect(() => {
    if (hasActiveHiddenFilters) {
      setExpanded(true);
    }
  }, [hasActiveHiddenFilters]);

  const handleChange = useCallback(
    ({ values }: { values: Record<string, any> }) => {
      isInternalChangeRef.current = true;

      const filteredValues = Object.fromEntries(
        Object.entries(values).filter(([, value]) => {
          if (value === null || value === undefined || value === "")
            return false;
          if (Array.isArray(value) && value.length === 0) return false;
          return true;
        })
      );

      onSubmit(filteredValues);
    },
    [onSubmit]
  );

  const handleClear = useCallback(() => {
    onSubmit({});
  }, [onSubmit]);

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  return (
    <Box>
      {title && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ minHeight: 48, mb: 2 }}
        >
          <Typography variant="h5">{title}</Typography>
          <Stack direction="row" spacing={1}>
            {hasValues && (
              <Tooltip title="Очистить фильтры">
                <Button
                  color="primary"
                  onClick={handleClear}
                  endIcon={<FilterAltOffIcon />}
                >
                  Очистить фильтры
                </Button>
              </Tooltip>
            )}
            {hasHiddenFields && (
              <Button
                color="primary"
                onClick={toggleExpanded}
                endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              >
                Фильтры
              </Button>
            )}
          </Stack>
        </Stack>
      )}

      <Form
        initialValues={controlledInitialValues}
        schema={currentSchema}
        onSubmit={() => Promise.resolve()}
        submitButton={false}
        resetButton={false}
        onChange={handleChange}
        subscription={{ values: true }}
        spacing={2}
      />
    </Box>
  );
}

interface AutoFilterWithQueryProps extends AutoFilterFormProps {
  query: any;
  variables?: Record<string, any>;
}

function AutoFilterWithQuery({
  query,
  variables,
  onSubmit,
  initialValues,
  schema,
  title,
}: AutoFilterWithQueryProps) {
  const { data, loading, error } = useQuery(query, { variables });

  if (loading) return <LinearProgress />;
  if (error) return <ErrorMessage error={error} />;

  return (
    <AutoFilterForm
      initialValues={initialValues}
      schema={typeof schema === "function" ? schema(data) : schema}
      onSubmit={onSubmit}
      title={title}
    />
  );
}

interface AutoFilterProps extends AutoFilterFormProps {
  query?: any;
  variables?: Record<string, any>;
  debounceMs?: number;
  resetKey?: number;
}

export function AutoFilter({
  query,
  variables,
  onSubmit,
  initialValues,
  schema,
  title,
}: AutoFilterProps) {
  if (query) {
    return (
      <AutoFilterWithQuery
        query={query}
        variables={variables}
        initialValues={initialValues}
        schema={schema}
        onSubmit={onSubmit}
        title={title}
      />
    );
  }

  return (
    <AutoFilterForm
      initialValues={initialValues}
      schema={schema}
      onSubmit={onSubmit}
      title={title}
    />
  );
}
